import { AccrualDocument } from "@common/dto/financialDocument/accrual-document-extraction.schema";
import { BillExtractionRecord } from "@common/schemas/financialDocument/bill-extraction-record.schema";
import { InvoiceExtractionRecord } from "@common/schemas/financialDocument/invoice-extraction-record.schema";
import { FinancialDocumentExtended } from "@common/types/financialDocument";
import { computeAccrualLineAmount } from "@common/helpers/accrual-line-item-amount.helper";
import { AccrualDocumentImporterMode } from "@components/accrualDocumentImporter/accrualDocumentImporter.lib";
import {
    getAllAccrualLineItemExtractions,
    resolveAccrualCounterpartyIdByName,
} from "@components/accrualDocumentImporter/accrualImporter.helpers";
import {
    rowsToBillExtractionRecord,
    rowsToInvoiceExtractionRecord,
} from "@components/accrualDocumentImporter/accrualDocumentRowSync.helpers";
import { uuidv7 } from "uuidv7";
import { accrualImporterExtractionIdFromPersisted } from "./accrualImporterExtractionId";
import { formatAccrualImporterDate } from "./accrualImporterDateFormat";
import { createAccrualImporterRow } from "./createAccrualImporterRow";
import { createAccrualImporterRowId } from "./createAccrualImporterRowId";
import { createAccrualImporterRowLineId } from "./accrualImporterLineId.helpers";
import { AccrualImporterRow } from "./types";

function buildSharedFieldsFromExtraction({
    extraction,
    financialDocument,
    counterpartiesMap,
}: {
    extraction: InvoiceExtractionRecord | BillExtractionRecord;
    financialDocument: FinancialDocumentExtended;
    counterpartiesMap: Map<string, string>;
}) {
    return {
        counterpartyId:
            extraction.counterpartyId ??
            resolveAccrualCounterpartyIdByName({
                counterpartyName: extraction.counterpartyName,
                counterpartiesMap,
            }),
        counterpartyName: extraction.counterpartyName,
        invoiceNumber: extraction.documentNumber ?? "",
        memo: extraction.memo ?? "",
        createdDate: formatAccrualImporterDate(extraction.documentDate),
        dueDate: formatAccrualImporterDate(extraction.dueDate),
        entityId: extraction.entityId ?? financialDocument.entityId ?? null,
        financialAccountId: financialDocument.financialAccountId ?? null,
        totalAmount: extraction.totalAmount,
        tax: extraction.taxAmount ?? undefined,
        extractionWarnings: extraction.warnings ?? undefined,
    };
}

function documentToAccrualExtractionRecord(
    document: AccrualDocument,
    extractionId: string,
): BillExtractionRecord {
    return {
        id: extractionId,
        counterpartyId: null,
        counterpartyIdSource: null,
        counterpartyName: document.counterpartyName,
        documentNumber: document.documentNumber,
        documentDate: document.documentDate,
        dueDate: document.dueDate,
        totalAmount: document.totalAmount,
        taxAmount: document.taxAmount ?? null,
        discountAmount: document.discountAmount ?? null,
        depositAmount: document.depositAmount ?? null,
        memo: document.memo ?? null,
        balanceDue: document.balanceDue ?? null,
        paymentDate: document.paymentDate ?? null,
        entityId: document.entityId,
        warnings: document.warnings,
        lineItems: document.lineItems.map((lineItem, index) => ({
            lineId: createAccrualImporterRowLineId({
                scopeId: extractionId,
                rowId: String(index + 1),
            }),
            item: lineItem.item,
            description: lineItem.description,
            amount: lineItem.amount,
            quantity: lineItem.quantity ?? null,
            unitPrice: lineItem.unitPrice ?? null,
            classIds: lineItem.classIds,
            accountId: lineItem.accountId,
            accrualAccountId: lineItem.accrualAccountId,
        })),
    };
}

function resolveExtractionsFromDocument({
    financialDocument,
    mode,
}: {
    financialDocument: FinancialDocumentExtended;
    mode: AccrualDocumentImporterMode;
}): InvoiceExtractionRecord[] | BillExtractionRecord[] {
    const persistedExtractions = getAllAccrualLineItemExtractions(
        financialDocument,
        mode,
    );
    if (persistedExtractions.length > 0) {
        return persistedExtractions;
    }

    const documentExtraction =
        mode === "bills"
            ? financialDocument.documentBillsExtraction
            : financialDocument.documentInvoicesExtraction;
    const sourceDocuments = documentExtraction?.originalExtraction?.documents;
    if (!sourceDocuments || sourceDocuments.length === 0) {
        return [];
    }

    return sourceDocuments.map((document) =>
        documentToAccrualExtractionRecord(document, uuidv7()),
    );
}

export function extractionsToRows({
    extractions,
    financialDocument,
    counterpartiesMap,
}: {
    extractions: InvoiceExtractionRecord[] | BillExtractionRecord[];
    financialDocument: FinancialDocumentExtended;
    counterpartiesMap: Map<string, string>;
}): AccrualImporterRow[] {
    const rows: AccrualImporterRow[] = [];

    for (const extraction of extractions) {
        const sharedFields = buildSharedFieldsFromExtraction({
            extraction,
            financialDocument,
            counterpartiesMap,
        });
        const lineItems =
            extraction.lineItems.length > 0 ? extraction.lineItems : [null];

        for (const lineItem of lineItems) {
            const quantity = lineItem?.quantity ?? null;
            const unitPrice = lineItem?.unitPrice ?? null;
            rows.push(
                createAccrualImporterRow({
                    _id: createAccrualImporterRowId(),
                    extractionId: accrualImporterExtractionIdFromPersisted(
                        extraction.id,
                    ),
                    lineId: lineItem?.lineId,
                    ...sharedFields,
                    productOrService: lineItem?.item ?? "",
                    catalogItemId: lineItem?.catalogItemId ?? null,
                    catalogItemPriceId: lineItem?.catalogItemPriceId ?? null,
                    description: lineItem?.description ?? "",
                    quantity,
                    unitPrice,
                    amount: lineItem
                        ? computeAccrualLineAmount({
                              quantity,
                              unitPrice,
                              amount: lineItem.amount,
                          })
                        : null,
                    classIds: lineItem?.classIds ?? null,
                    accrualLedger: lineItem?.accountId ?? "",
                    accrualAccountId: lineItem?.accrualAccountId ?? "",
                }),
            );
        }
    }

    return rows;
}

export function rowsToInvoiceExtractions({
    rows,
    counterpartiesMap,
    existingExtractions,
}: {
    rows: AccrualImporterRow[];
    counterpartiesMap: Map<string, string>;
    existingExtractions?: InvoiceExtractionRecord[];
}): InvoiceExtractionRecord[] {
    const rowsByExtractionId = groupRowsByExtractionId(rows);
    const existingById = new Map(
        (existingExtractions ?? []).map((extraction) => [
            extraction.id,
            extraction,
        ]),
    );

    return Array.from(rowsByExtractionId.entries()).map(
        ([extractionId, groupRows]) =>
            rowsToInvoiceExtractionRecord({
                rows: groupRows,
                extractionId,
                counterpartiesMap,
                existingExtraction: existingById.get(extractionId) ?? null,
            }),
    );
}

export function rowsToBillExtractions({
    rows,
    counterpartiesMap,
    existingExtractions,
}: {
    rows: AccrualImporterRow[];
    counterpartiesMap: Map<string, string>;
    existingExtractions?: BillExtractionRecord[];
}): BillExtractionRecord[] {
    const rowsByExtractionId = groupRowsByExtractionId(rows);
    const existingById = new Map(
        (existingExtractions ?? []).map((extraction) => [
            extraction.id,
            extraction,
        ]),
    );

    return Array.from(rowsByExtractionId.entries()).map(
        ([extractionId, groupRows]) =>
            rowsToBillExtractionRecord({
                rows: groupRows,
                extractionId,
                counterpartiesMap,
                existingExtraction: existingById.get(extractionId) ?? null,
            }),
    );
}

export function groupRowsByExtractionId(
    rows: AccrualImporterRow[],
): Map<string, AccrualImporterRow[]> {
    const groups = new Map<string, AccrualImporterRow[]>();

    for (const row of rows) {
        const groupRows = groups.get(row.extractionId) ?? [];
        groupRows.push(row);
        groups.set(row.extractionId, groupRows);
    }

    return groups;
}

export function mapExtractionToAccrualImporterRows({
    financialDocument,
    mode,
    counterpartiesMap,
}: {
    financialDocument: FinancialDocumentExtended;
    mode: AccrualDocumentImporterMode;
    counterpartiesMap: Map<string, string>;
}): AccrualImporterRow[] {
    const extractions = resolveExtractionsFromDocument({
        financialDocument,
        mode,
    });

    if (extractions.length === 0) {
        return [
            createAccrualImporterRow({
                _id: createAccrualImporterRowId(),
                entityId: financialDocument.entityId ?? null,
                financialAccountId:
                    financialDocument.financialAccountId ?? null,
            }),
        ];
    }

    return extractionsToRows({
        extractions,
        financialDocument,
        counterpartiesMap,
    });
}
